import { Button } from '@components/atoms/Button/Button';
import { LoadingIndicator } from '@components/atoms/LoadingIndicator/LoadingIndicator';
import type { TvChannelDay } from '@lib/graphql/generated';
import { IconCarretRight } from '@src/assets/icon-carret-right';
import { useTvGuideStore } from '@src/stores/tvguide';
import { useScrollSync } from '@src/utils/useScrollSync';
import { differenceInMinutes, isAfter, isSameDay, isToday, set, startOfDay } from 'date-fns';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { TIMESCALE } from 'tvm-config';
import { ChannelBase, TimeBar, TimelineChannelProgram } from './';

const TimeIndicator = dynamic(() => import('./TimeIndicator').then((mod) => mod.TimeIndicator), { ssr: false });

export interface TimelineViewProps {
  channelEntries: TvChannelDay[];
  activeDay: Date;
  fetching?: boolean;
  hasMorePages?: boolean;
  currentPage?: number;
  onFetchMore?: () => void;
}

export const TimelineView = ({ channelEntries, activeDay, fetching, currentPage, hasMorePages, onFetchMore }) => {
  const now = useTvGuideStore((state) => state.tvCurrentTime);
  const [initiallyScrolled, setInitiallyScrolled] = useState(false);
  const scrollMargin = () => (window?.innerWidth > 767 ? 300 : 50);
  const documentRef = useRef<Document>(null);

  const [draggingBlocked, setDraggingBlocked] = useState(false);
  const dragPositions = useRef({
    left: null,
    top: null,
    x: null,
    y: null,
  });
  const isDragging = useRef(false);
  const dragDelta = useRef({ dx: 0, dy: 0 });
  const dragRaf = useRef<number | null>(null);

  const [scrollWindow, setScrollWindow] = useState({
    start: Number.NEGATIVE_INFINITY,
    end: Number.POSITIVE_INFINITY,
  });
  const windowRaf = useRef<number | null>(null);
  const lastWindowScroll = useRef<number | null>(null);
  const dragListeners = useRef({ move: mouseMoveListener, up: mouseUpListener });
  const onDragMove = useRef((evt: MouseEvent) => dragListeners.current.move(evt)).current;
  const onDragUp = useRef(() => dragListeners.current.up()).current;

  const primeTime = new Date(activeDay.getFullYear(), activeDay.getMonth(), activeDay.getDate(), 20, 15, 0, 0);

  const timelineScrollElement = useRef(null);
  const showtimeScrollElement = useRef(null);
  const syncedScroll = useScrollSync(timelineScrollElement, showtimeScrollElement);

  const firstFetchDate = useMemo(() => startOfDay(activeDay), [activeDay]);

  const recomputeWindow = useCallback(() => {
    windowRaf.current = null;
    const el = showtimeScrollElement.current;
    if (!el) return;
    const view = el.clientWidth || 0;
    const buffer = Math.max(view * 0.75, 600);
    lastWindowScroll.current = el.scrollLeft;
    const start = el.scrollLeft - buffer;
    const end = el.scrollLeft + view + buffer;
    setScrollWindow((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
  }, []);

  const scheduleWindowUpdate = useCallback(() => {
    const el = showtimeScrollElement.current;
    if (!el) return;
    const view = el.clientWidth || 1;
    if (lastWindowScroll.current !== null && Math.abs(el.scrollLeft - lastWindowScroll.current) < view * 0.4) {
      return;
    }
    if (windowRaf.current !== null) return;
    windowRaf.current = window.requestAnimationFrame(recomputeWindow);
  }, [recomputeWindow]);

  function applyDragFrame() {
    dragRaf.current = null;
    const { dx, dy } = dragDelta.current;
    const left = dragPositions.current.left - dx;
    if (showtimeScrollElement.current) {
      showtimeScrollElement.current.scrollLeft = left;
    }
    if (timelineScrollElement.current) {
      timelineScrollElement.current.scrollLeft = left;
    }
    document.documentElement.scrollTop = dragPositions.current.top - dy;
  }

  function clearDragState() {
    if (dragRaf.current !== null) {
      window.cancelAnimationFrame(dragRaf.current);
      dragRaf.current = null;
    }
    if (isDragging.current) {
      isDragging.current = false;
      showtimeScrollElement.current?.classList.remove('cursor-move', '*:pointer-events-none');
      showtimeScrollElement.current?.classList.add('cursor-pointer');
    }
  }

  function mouseMoveListener(evt) {
    const dx = evt.clientX - dragPositions.current.x;
    const dy = evt.clientY - dragPositions.current.y;
    dragDelta.current = { dx, dy };
    if (!isDragging.current && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) {
      isDragging.current = true;
      showtimeScrollElement.current?.classList.remove('cursor-pointer');
      showtimeScrollElement.current?.classList.add('cursor-move', '*:pointer-events-none');
    }
    if (dragRaf.current === null) {
      dragRaf.current = window.requestAnimationFrame(applyDragFrame);
    }
  }

  function removeEventListeners() {
    documentRef.current?.removeEventListener('mousemove', onDragMove);
    documentRef.current?.removeEventListener('mouseup', onDragUp);
  }

  function mouseUpListener() {
    if (typeof window !== 'undefined') {
      document.documentElement.style.scrollBehavior = 'smooth';
    }

    setTimeout(() => {
      removeEventListeners();
      clearDragState();
    }, 1);
  }

  dragListeners.current.move = mouseMoveListener;
  dragListeners.current.up = mouseUpListener;

  function handleMouseDown(evt) {
    if (draggingBlocked) return;
    window.requestAnimationFrame(() => {
      documentRef.current.addEventListener('mousemove', onDragMove);
      documentRef.current.addEventListener('mouseup', onDragUp);
    });
    if (showtimeScrollElement) {
      document.documentElement.style.scrollBehavior = 'auto';
    }
    dragPositions.current = {
      left: showtimeScrollElement.current.scrollLeft,
      top: document.documentElement.scrollTop,
      x: evt.clientX,
      y: evt.clientY,
    };
  }

  function scrollTimelineWindow(direction) {
    syncedScroll(showtimeScrollElement.current.scrollLeft + direction * window.innerWidth * 0.8, true);
  }

  function scrollToPrimeTime() {
    if (showtimeScrollElement.current) {
      setDraggingBlocked(true);
      clearDragState();
      removeEventListeners();
      syncedScroll(differenceInMinutes(primeTime, firstFetchDate) * TIMESCALE - scrollMargin(), true);
      setTimeout(() => {
        setDraggingBlocked(false);
        setInitiallyScrolled(true);
      }, 1000);
    }
  }

  const scrollToCurrent = useCallback(() => {
    if (showtimeScrollElement.current) {
      syncedScroll(differenceInMinutes(now, firstFetchDate) * TIMESCALE - scrollMargin(), initiallyScrolled);

      setTimeout(() => {
        setInitiallyScrolled(true);
      }, 1000);
    }
    // biome-ignore lint/correctness/useExhaustiveDependencies: scrollMargin is a stable inline function
  }, [firstFetchDate, initiallyScrolled, now, syncedScroll, scrollMargin]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: mouseUpListener is a stable inline function
  useEffect(() => {
    const unsub = useTvGuideStore.subscribe(
      (state) => state.openEntryData,
      () => {
        mouseUpListener();
      },
    );
    return unsub;
  }, []);

  useEffect(() => {
    const el = showtimeScrollElement.current;
    if (!el) return;
    el.addEventListener('scroll', scheduleWindowUpdate, { passive: true });
    window.addEventListener('resize', recomputeWindow);
    const raf = window.requestAnimationFrame(recomputeWindow);
    return () => {
      el.removeEventListener('scroll', scheduleWindowUpdate);
      window.removeEventListener('resize', recomputeWindow);
      window.cancelAnimationFrame(raf);
      if (windowRaf.current !== null) window.cancelAnimationFrame(windowRaf.current);
    };
  }, [scheduleWindowUpdate, recomputeWindow]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: inline functions intentionally excluded to prevent infinite loops
  useEffect(() => {
    documentRef.current = document;
    setTimeout(() => {
      isToday(activeDay) ? scrollToCurrent() : scrollToPrimeTime();
    }, 1);

    return () => {
      removeEventListeners();
    };
  }, [activeDay]);

  return (
    <>
      <div
        className={twMerge(
          'sticky top-28 z-30 w-full lg:top-20',
          draggingBlocked && 'pointer-events-none! *:pointer-events-none!',
        )}
      >
        <div className="absolute inset-x-0 top-0 flex h-10 justify-between bg-black text-white">
          <button
            type="button"
            className="z-10 grid h-10 w-16 items-center bg-linear-to-r from-black via-70% via-black to-black/0 pr-4 pl-2"
            onClick={() => {
              setTimeout(() => {
                scrollTimelineWindow(-1);
              });
            }}
          >
            <IconCarretRight classProps={{ root: 'rotate-180' }} />
          </button>
          <button
            type="button"
            className="z-10 grid h-10 w-16 items-center justify-end bg-linear-to-l from-black via-70% via-black to-black/0 pr-2 pl-4"
            onClick={() => {
              setTimeout(() => {
                scrollTimelineWindow(1);
              });
            }}
          >
            <IconCarretRight />
          </button>
        </div>
        <div
          className="scrollbar-none relative -mb-3 overflow-x-auto overflow-y-hidden pb-3 will-change-scroll"
          ref={timelineScrollElement}
        >
          <TimeIndicator
            minutesSinceStart={Math.floor((primeTime.getTime() - firstFetchDate.getTime()) / 1000 / 60)}
            onAction={() => setTimeout(scrollToPrimeTime)}
            text="20:15"
            classProps={{
              root: 'ml-24 md:ml-52',
              leftBorder:
                isSameDay(activeDay, now) && isAfter(set(new Date(), { hours: 20, minutes: 15, seconds: 0 }), now)
                  ? 'left-33'
                  : 'left-16',
              rightBorder: isAfter(set(new Date(), { hours: 20, minutes: 15, seconds: 0 }), now)
                ? 'right-16'
                : 'right-33',
              chip: draggingBlocked && 'pointer-events-none',
            }}
          />
          {isSameDay(activeDay, now) && (
            <TimeIndicator
              minutesSinceStart={Math.floor((now.getTime() - firstFetchDate.getTime()) / 1000 / 60)}
              onAction={() => setTimeout(scrollToCurrent)}
              text="Jetzt"
              classProps={{
                root: 'ml-24 md:ml-52',
                rightBorder: isAfter(set(new Date(), { hours: 20, minutes: 15, seconds: 0 }), now)
                  ? 'right-33'
                  : 'right-16',
                leftBorder: isAfter(set(new Date(), { hours: 20, minutes: 15, seconds: 0 }), now)
                  ? 'left-16'
                  : 'left-33',
              }}
            />
          )}
          <TimeBar firstFetchDate={firstFetchDate} />
        </div>
      </div>
      <div className="absolute left-0 z-20 space-y-1 bg-black pr-1 shadow-xl">
        {channelEntries?.map((entry) => (
          <ChannelBase key={entry?.channel.id} channel={entry?.channel} bookmarked={!!entry?.channel?.favorite?.id} />
        ))}
      </div>
      <div
        className="scrollbar-none relative left-0 ml-24 cursor-pointer select-none overflow-x-auto overflow-y-hidden transition duration-1000 will-change-scroll md:ml-52"
        ref={showtimeScrollElement}
        role="presentation"
        onMouseDown={handleMouseDown}
      >
        <div
          className="absolute inset-y-0 left-0"
          style={{
            width: `${30 * 60 * TIMESCALE}px`,
          }}
        >
          {fetching && currentPage === null && (
            <div className="sticky left-0 z-40 grid min-h-full w-48 items-center md:w-100">
              {channelEntries?.map((_e, index) => (
                <LoadingIndicator visible classProps={{ root: 'h-[72px] md:h-28 min-h-0' }} key={index} />
              ))}
            </div>
          )}
        </div>
        <div
          className="relative space-y-1 overflow-x-clip"
          style={{
            width: `${30 * 60 * TIMESCALE}px`,
          }}
        >
          {channelEntries?.map((entry) => (
            <TimelineChannelProgram
              key={entry?.channel.id}
              channelEntry={entry}
              activeDay={activeDay}
              currentTime={now}
              windowStart={scrollWindow.start}
              windowEnd={scrollWindow.end}
            />
          ))}
          <div className="pointer-events-none absolute -top-2 bottom-0 w-36 bg-linear-to-r from-black to-black/0" />
          <div className="pointer-events-none absolute -top-2 right-0 bottom-0 w-36 bg-linear-to-l from-black to-black/0" />
        </div>
        {isSameDay(activeDay, now) && (
          <TimeIndicator
            minutesSinceStart={Math.floor((now.getTime() - firstFetchDate.getTime()) / 1000 / 60)}
            onAction={scrollToCurrent}
            onlyLine
          />
        )}
      </div>
      {hasMorePages && (
        <div className="text-center">
          <Button
            title="Mehr laden"
            hasAction
            onAction={() => setTimeout(onFetchMore)}
            buttonStyle="secondary"
            isLoading={fetching}
            classProps={{ root: 'mt-6' }}
          />
        </div>
      )}
    </>
  );
};
